import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RenderIcon } from "./IconPickerPopover";
import { Plus, Trash } from "@phosphor-icons/react";
import { ArrowUp, ArrowDown } from "@phosphor-icons/react";
import {
  ModulePricing,
  ModuleFunctionality,
  TierPricing,
} from "../types";

interface FunctionalityEditorProps {
  modulePricing: ModulePricing;
  tierPricing: TierPricing;
  onModulePricingChange: (mp: ModulePricing) => void;
}

export const FunctionalityEditor = ({
  modulePricing,
  tierPricing,
  onModulePricingChange,
}: FunctionalityEditorProps) => {
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const tierKeys = Object.keys(tierPricing);
  const allModuleKeys = Object.keys(modulePricing);
  const enabledModules = allModuleKeys.filter(
    (k) => modulePricing[k].enabled
  );

  const moveModule = (modKey: string, direction: -1 | 1) => {
    // Work with enabled modules list to find the swap target
    const enabledIdx = enabledModules.indexOf(modKey);
    const swapIdx = enabledIdx + direction;
    if (swapIdx < 0 || swapIdx >= enabledModules.length) return;
    const swapKey = enabledModules[swapIdx];

    // Rebuild the full key list with these two swapped
    const newKeys = [...allModuleKeys];
    const idxA = newKeys.indexOf(modKey);
    const idxB = newKeys.indexOf(swapKey);
    [newKeys[idxA], newKeys[idxB]] = [newKeys[idxB], newKeys[idxA]];

    const reordered: ModulePricing = {};
    for (const k of newKeys) reordered[k] = modulePricing[k];
    onModulePricingChange(reordered);
  };

  const updateFunctionalities = (
    moduleKey: string,
    funcs: ModuleFunctionality[]
  ) => {
    onModulePricingChange({
      ...modulePricing,
      [moduleKey]: { ...modulePricing[moduleKey], functionalities: funcs },
    });
  };

  const addFunctionality = (moduleKey: string) => {
    const funcs = [...(modulePricing[moduleKey].functionalities || [])];
    const tierDefaults: Record<string, string> = {};
    tierKeys.forEach((t) => (tierDefaults[t] = ""));
    funcs.push({
      id: crypto.randomUUID(),
      label: "Nieuwe functionaliteit",
      tiers: tierDefaults,
    });
    updateFunctionalities(moduleKey, funcs);
  };

  const updateFunc = (
    moduleKey: string,
    funcId: string,
    patch: Partial<ModuleFunctionality>
  ) => {
    const funcs = (modulePricing[moduleKey].functionalities || []).map((f) =>
      f.id === funcId ? { ...f, ...patch } : f
    );
    updateFunctionalities(moduleKey, funcs);
  };

  const updateFuncTier = (
    moduleKey: string,
    funcId: string,
    tierKey: string,
    value: string
  ) => {
    const funcs = (modulePricing[moduleKey].functionalities || []).map((f) =>
      f.id === funcId ? { ...f, tiers: { ...f.tiers, [tierKey]: value } } : f
    );
    updateFunctionalities(moduleKey, funcs);
  };

  const removeFunc = (moduleKey: string, funcId: string) => {
    const funcs = (modulePricing[moduleKey].functionalities || []).filter(
      (f) => f.id !== funcId
    );
    updateFunctionalities(moduleKey, funcs);
  };

  const moveFunc = (moduleKey: string, funcId: string, direction: -1 | 1) => {
    const funcs = [...(modulePricing[moduleKey].functionalities || [])];
    const idx = funcs.findIndex((f) => f.id === funcId);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= funcs.length) return;
    [funcs[idx], funcs[swapIdx]] = [funcs[swapIdx], funcs[idx]];
    updateFunctionalities(moduleKey, funcs);
  };

  return (
    <div className="space-y-4">
      {enabledModules.map((modKey) => {
        const mod = modulePricing[modKey];
        const funcs = mod.functionalities || [];
        const isExpanded = expandedModule === modKey;

        return (
          <div key={modKey} className="rounded-lg border bg-card">
            <div className="flex items-center p-3 hover:bg-muted/30 transition-colors">
              <button
                onClick={() => setExpandedModule(isExpanded ? null : modKey)}
                className="flex items-center gap-2 flex-1 min-w-0"
              >
                <RenderIcon value={mod.icon} size={20} />
                <span className="font-semibold text-sm">{mod.label}</span>
                <span className="text-[10px] text-neutral-secondary-mid">
                  {funcs.length} functionaliteit(en)
                </span>
              </button>
              <div className="flex items-center gap-0.5 mr-2">
                {enabledModules.indexOf(modKey) > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-neutral-secondary-mid hover:text-primary"
                    onClick={(e) => { e.stopPropagation(); moveModule(modKey, -1); }}>
                    <ArrowUp size={12} />
                  </Button>
                )}
                {enabledModules.indexOf(modKey) < enabledModules.length - 1 && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-neutral-secondary-mid hover:text-primary"
                    onClick={(e) => { e.stopPropagation(); moveModule(modKey, 1); }}>
                    <ArrowDown size={12} />
                  </Button>
                )}
              </div>
              <button onClick={() => setExpandedModule(isExpanded ? null : modKey)}
                className="text-xs text-neutral-secondary-mid">
                {isExpanded ? "\u25B2" : "\u25BC"}
              </button>
            </div>

            {isExpanded && (
              <div className="px-3 pb-3">
                {funcs.length > 0 ? (
                  <div className="space-y-2">
                    {funcs.map((func) => {
                      const defVal = func.defaultValue || "";

                      const allCells: { key: string; label: string; value: string; onValChange: (v: string) => void; highlight?: boolean }[] = [
                        { key: "_default", label: "Standaard (bij nieuwe offertes)", value: defVal, onValChange: (v) => updateFunc(modKey, func.id, { defaultValue: v }), highlight: true },
                        ...tierKeys.map((t) => ({
                          key: t,
                          label: `${tierPricing[t]?.icon || ""} ${tierPricing[t]?.label || t}`,
                          value: func.tiers[t] || "",
                          onValChange: (v: string) => updateFuncTier(modKey, func.id, t, v),
                        })),
                      ];

                      return (
                        <div key={func.id} className="rounded-md border p-3 group">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex flex-col gap-0.5 flex-shrink-0">
                              {funcs.indexOf(func) > 0 && (
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-neutral-secondary-mid hover:text-primary"
                                  onClick={() => moveFunc(modKey, func.id, -1)}>
                                  <ArrowUp size={10} />
                                </Button>
                              )}
                              {funcs.indexOf(func) < funcs.length - 1 && (
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-neutral-secondary-mid hover:text-primary"
                                  onClick={() => moveFunc(modKey, func.id, 1)}>
                                  <ArrowDown size={10} />
                                </Button>
                              )}
                            </div>
                            <Input
                              value={func.label}
                              onChange={(e) => updateFunc(modKey, func.id, { label: e.target.value })}
                              className="h-9 text-sm font-medium flex-1"
                              placeholder="Functionaliteit naam..."
                            />
                            <Button variant="ghost" size="sm"
                              className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-destructive/50 hover:text-destructive flex-shrink-0"
                              onClick={() => removeFunc(modKey, func.id)}>
                              <Trash size={14} />
                            </Button>
                          </div>
                          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${1 + tierKeys.length}, minmax(0, 1fr))` }}>
                            {allCells.map((cell) => {
                              const isCheck = cell.value === "\u2714";
                              const isEmpty = cell.value === "";
                              return (
                                <div key={cell.key}>
                                  <p className={`text-[9px] mb-1 ${cell.highlight ? "font-bold text-primary" : "text-neutral-secondary-mid"}`}>{cell.label}</p>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => {
                                        if (isEmpty) cell.onValChange("\u2714");
                                        else if (isCheck) cell.onValChange("");
                                        else cell.onValChange("");
                                      }}
                                      className={`w-8 h-8 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors text-sm ${
                                        isCheck ? (cell.highlight ? "bg-primary/10 border-primary text-primary" : "bg-emerald-100 border-emerald-300 text-emerald-700") :
                                        isEmpty ? "bg-gray-50 border-gray-200 text-gray-300" :
                                        "bg-blue-50 border-blue-200 text-blue-600"
                                      }`}
                                    >
                                      {isCheck ? "\u2714" : isEmpty ? "\u2014" : "T"}
                                    </button>
                                    <Input
                                      value={isCheck ? "" : cell.value}
                                      onChange={(e) => cell.onValChange(e.target.value)}
                                      placeholder="Tekst..."
                                      className="h-8 text-sm flex-1"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-neutral-secondary-mid py-2 text-center">
                    Nog geen functionaliteiten. Voeg er een toe.
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addFunctionality(modKey)}
                  className="gap-1.5 text-xs mt-2"
                >
                  <Plus size={12} /> Functionaliteit toevoegen
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
